import { useCallback } from 'react';
import { useUpdateStore } from '../stores/update-store';

const ACCENT_COLOR = 'oklch(0.74 0.11 65)';

/**
 * Non-disruptive update notification banner.
 * Positioned below the TopBar (top: 36), same z-layer as ConnectionBanner.
 *
 * States:
 * - Available: shows version + "Update Now" / "Later"
 * - Downloading: shows progress bar
 * - Error: shows error + "Retry"
 */
export function UpdateBanner() {
	const { available, version, downloading, progress, error, dismissed } = useUpdateStore();

	const handleUpdateNow = useCallback(async () => {
		const store = useUpdateStore.getState();
		store.setDownloading(true);
		store.setError(null as unknown as string);
		try {
			const { check } = await import('@tauri-apps/plugin-updater');
			const update = await check();
			if (update) {
				let downloaded = 0;
				let contentLength = 0;
				await update.downloadAndInstall((event) => {
					switch (event.event) {
						case 'Started':
							contentLength = (event.data as { contentLength?: number }).contentLength ?? 0;
							break;
						case 'Progress':
							downloaded += (event.data as { chunkLength: number }).chunkLength;
							if (contentLength > 0) {
								store.setProgress(Math.round((downloaded / contentLength) * 100));
							}
							break;
						case 'Finished':
							store.setProgress(100);
							break;
					}
				});
				// Restart the app to apply the update
				const tauri = (window as any).__TAURI__;
				if (tauri?.core?.invoke) {
					tauri.core.invoke('quit_app');
				}
			}
		} catch (err: any) {
			store.setError(err?.message || 'Update failed');
			store.setDownloading(false);
		}
	}, []);

	const handleDismiss = useCallback(() => {
		useUpdateStore.getState().dismiss();
	}, []);

	const handleRetry = useCallback(() => {
		useUpdateStore.getState().setError(null as unknown as string);
		handleUpdateNow();
	}, [handleUpdateNow]);

	if (!available || dismissed) return null;

	let text: string;
	let actions: { label: string; onClick: () => void; primary?: boolean }[] = [];

	if (error) {
		text = `Update failed: ${error}`;
		actions = [{ label: 'Retry', onClick: handleRetry, primary: true }];
	} else if (downloading) {
		text = progress > 0 ? `Downloading update\u2026 ${progress}%` : 'Downloading update\u2026';
	} else {
		text = `Khal OS v${version} is available`;
		actions = [
			{ label: 'Later', onClick: handleDismiss },
			{ label: 'Update Now', onClick: handleUpdateNow, primary: true },
		];
	}

	return (
		<div
			style={{
				position: 'fixed',
				top: 36,
				left: 0,
				right: 0,
				zIndex: 9400,
				height: 32,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				gap: 12,
				background: 'var(--khal-surface)',
				backdropFilter: 'blur(20px) saturate(1.8)',
				WebkitBackdropFilter: 'blur(20px) saturate(1.8)',
				borderLeft: `4px solid ${ACCENT_COLOR}`,
				borderBottom: '1px solid var(--khal-taskbar-border)',
				fontSize: 13,
				color: 'var(--khal-text-primary)',
			}}
		>
			<span>{text}</span>

			{/* Progress bar during download */}
			{downloading && !error && (
				<div
					style={{
						width: 120,
						height: 4,
						borderRadius: 9999,
						background: 'rgba(255,255,255,0.08)',
						overflow: 'hidden',
					}}
				>
					<div
						style={{
							width: `${progress}%`,
							height: '100%',
							borderRadius: 9999,
							background: ACCENT_COLOR,
							boxShadow: `0 0 8px ${ACCENT_COLOR}`,
							transition: 'width 0.3s ease',
						}}
					/>
				</div>
			)}

			{/* Action buttons */}
			{actions.map((action) => (
				<button
					key={action.label}
					type="button"
					onClick={action.onClick}
					style={{
						fontSize: 12,
						fontWeight: 500,
						color: action.primary ? ACCENT_COLOR : 'var(--khal-text-secondary)',
						background: action.primary
							? `color-mix(in srgb, ${ACCENT_COLOR} 12%, transparent)`
							: 'rgba(255,255,255,0.06)',
						border: action.primary
							? `1px solid color-mix(in srgb, ${ACCENT_COLOR} 30%, transparent)`
							: '1px solid rgba(255,255,255,0.1)',
						borderRadius: 9999,
						padding: '2px 10px',
						cursor: 'default',
						lineHeight: '18px',
					}}
				>
					{action.label}
				</button>
			))}
		</div>
	);
}
